/**
 * lx-page-settings —— 设置页
 *
 * 五组设置，全部**真实可用**（无 mock 原则 D5）：
 *   外观  —— 主题 + 字号，改一处全局生效
 *   数据  —— 载入演示数据 / 清空全部数据，操作真实的 IndexedDB
 *   AI    —— 服务地址 / Key / 模型名 + 连通性测试，真实发起请求
 *   语音  —— 豆包流式 ASR 凭据 + 转发服务地址，真实建立 WebSocket
 *
 * M4 把语音组从「待实现」变成了真配置 —— 因为 M4 确实实现了它。
 */

import { LxElement, define } from '@/core/base-element';
import {
  settingsRepo,
  applyTheme,
  applyFontScale,
  FONT_SCALE_PRESETS,
} from '@/settings/settings';
import { loadDemoData } from '@/data/demo-data';
import { dbClearAll, dbCount, STORE } from '@/data/db';
import { eventBus } from '@/core/event-bus';
import { testConnection, AIError } from '@/ai/client';
import type { ThemeMode } from '@/types/models';

const THEME_OPTIONS: { label: string; value: ThemeMode }[] = [
  { label: '浅色', value: 'light' },
  { label: '深色', value: 'dark' },
  { label: '跟随系统', value: 'auto' },
];

/** 与 demo-data.ts 的条目数保持一致。写在这里是为了让按钮文案不改 demo 模块 */
const DEMO_COUNT = 8;

export class LxPageSettings extends LxElement {
  private _busy = false;
  /** 底部那行数据统计 */
  private _stat = '';
  /** AI 连通性测试的状态：闲置 / 测试中 / 结果 */
  private _aiTest: { state: 'idle' | 'testing' | 'ok' | 'fail'; text: string } = {
    state: 'idle',
    text: '',
  };

  /** 重新统计各表条数，展示在数据分组下方 */
  private async refreshStat(): Promise<void> {
    const [contacts, sessions, messages, files] = await Promise.all([
      dbCount(STORE.contacts),
      dbCount(STORE.sessions),
      dbCount(STORE.messages),
      dbCount(STORE.files),
    ]);
    this._stat =
      contacts === 0 && sessions === 0
        ? '数据库为空。数据全部保存在本机浏览器里，不会上传。'
        : `本机现有 ${contacts} 位联系人、${sessions} 个会话、${messages} 条消息、${files} 个文件。`;
  }
  protected styles(): string {
    return `
      :host {
        display: block;
        padding-bottom: var(--sp-6);
      }

      .group {
        margin-top: var(--sp-4);
      }

      .group-title {
        padding: 0 var(--layout-content-pad-x) var(--sp-2);
        font-size: var(--fs-xs);
        color: var(--c-fg-muted);
        letter-spacing: 0.06em;
      }

      .card {
        margin: 0 var(--layout-content-pad-x);
        background: var(--c-surface);
        border: 1px solid var(--c-border);
        border-radius: var(--radius-md);
        overflow: hidden;
      }

      .row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--sp-3);
        min-height: 48px;
        padding: var(--sp-2) var(--sp-4);
        font-size: var(--fs-md);
      }

      .row + .row {
        border-top: 1px solid var(--c-border);
      }

      .row-label {
        color: var(--c-fg);
      }

      /* ---------- 开关（调试模式） ---------- */
      .switch {
        flex-shrink: 0;
        width: 44px;
        height: 26px;
        padding: 3px;
        border-radius: var(--radius-full);
        background: var(--c-surface-3);
        border: 1px solid var(--c-border);
        display: flex;
        align-items: center;
        transition: background var(--dur-fast) var(--ease-standard);
      }

      .switch .knob {
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: var(--c-surface);
        box-shadow: var(--shadow-sm);
        transition: transform var(--dur-fast) var(--ease-standard);
      }

      .switch[aria-checked='true'] {
        background: var(--c-primary);
        border-color: var(--c-primary);
      }

      .switch[aria-checked='true'] .knob {
        transform: translateX(18px);
      }

      /* ---------- 分段控件（主题 / 字号） ---------- */
      .segment {
        display: flex;
        gap: 2px;
        padding: 2px;
        background: var(--c-surface-2);
        border-radius: var(--radius-full);
      }

      .segment button {
        padding: var(--sp-1) var(--sp-3);
        border-radius: var(--radius-full);
        font-size: var(--fs-xs);
        color: var(--c-fg-muted);
        transition: all var(--dur-fast) var(--ease-standard);
        white-space: nowrap;
      }

      .segment button[aria-pressed='true'] {
        background: var(--c-accent);
        color: var(--c-accent-fg);
        font-weight: var(--fw-medium);
      }

      /* ---------- 可点击的操作行（数据分组） ---------- */
      button.row {
        width: 100%;
        text-align: left;
      }

      button.row:hover { background: var(--c-surface-2); }
      button.row:disabled { opacity: .55; cursor: not-allowed; }

      .row-value {
        display: flex;
        align-items: center;
        gap: var(--sp-2);
        font-size: var(--fs-sm);
        color: var(--c-fg-muted);
        flex-shrink: 0;
      }

      .row-value .arrow {
        width: 16px;
        height: 16px;
        fill: none;
        stroke: currentColor;
        stroke-width: 1.8;
        opacity: .5;
      }

      .danger-text { color: var(--c-danger); }

      /* ---------- 带输入框的行（AI 配置） ---------- */
      .row.field {
        flex-direction: column;
        align-items: stretch;
        gap: var(--sp-2);
        padding: var(--sp-3) var(--sp-4);
        min-height: 0;
      }

      .row.field .row-label {
        font-size: var(--fs-sm);
        color: var(--c-fg-muted);
      }

      .row.field input {
        width: 100%;
        padding: var(--sp-2) var(--sp-3);
        border: 1px solid var(--c-border);
        border-radius: var(--radius-sm);
        background: var(--c-surface-2);
        color: var(--c-fg);
        font-size: var(--fs-sm);
        font-family: var(--font-mono);
      }

      .row.field input:focus {
        outline: none;
        border-color: var(--c-accent);
      }

      .row.field input::placeholder {
        color: var(--c-fg-subtle);
        font-family: var(--font-sans);
      }

      .hint {
        font-size: var(--fs-xs);
        color: var(--c-fg-subtle);
        line-height: var(--lh-base);
      }

      /* ---------- 主按钮行（测试连接） ----------
         用 --c-primary 而不是 --c-accent：
         荧光本体（#CCFF00）在浅色主题下是「点缀色」，只用于徽标/
         指示条/聚焦环这类小面积元素。整条通栏按钮是页面级主操作，
         必须用 --c-primary（浅色下是暗绿 #5C7A00）才能承载白字、
         并与输入栏的发送按钮保持同一套主色语言。 */
      .row.action {
        justify-content: center;
        font-weight: var(--fw-medium);
        color: var(--c-primary-fg);
        background: var(--c-primary);
        letter-spacing: 0.02em;
      }

      .row.action:hover { filter: brightness(1.1); }
      .row.action:disabled { opacity: .6; cursor: wait; filter: none; }

      /* ---------- 测试结果反馈 ---------- */
      .feedback {
        padding: var(--sp-2) var(--sp-4);
        font-size: var(--fs-sm);
        line-height: var(--lh-base);
        border-top: 1px solid var(--c-border);
      }

      .feedback.ok { color: var(--c-success); background: var(--c-success-soft); }
      .feedback.fail { color: var(--c-danger); background: var(--c-danger-soft); }

      .notice-inline {
        margin: var(--sp-3) var(--layout-content-pad-x) 0;
        padding: var(--sp-2) var(--sp-3);
        border-radius: var(--radius-sm);
        background: var(--c-warning-soft);
        color: var(--c-warning);
        font-size: var(--fs-xs);
        line-height: var(--lh-base);
      }

      .stat {
        margin: var(--sp-3) var(--layout-content-pad-x) 0;
        font-size: var(--fs-xs);
        color: var(--c-fg-subtle);
        line-height: var(--lh-base);
      }

      .foot {
        margin: var(--sp-5) var(--layout-content-pad-x) 0;
        font-size: var(--fs-xs);
        color: var(--c-fg-subtle);
        line-height: var(--lh-base);
        text-align: center;
      }
    `;
  }

  protected render(): string {
    const app = settingsRepo.getApp();

    return `
      <section class="group">
        <h2 class="group-title">外观</h2>
        <div class="card">
          <div class="row">
            <span class="row-label">主题</span>
            <div class="segment" data-group="theme" role="group" aria-label="主题">
              ${THEME_OPTIONS.map(
                (o) => `
                <button type="button" data-value="${o.value}"
                  aria-pressed="${app.theme === o.value}">${o.label}</button>
              `,
              ).join('')}
            </div>
          </div>
          <div class="row">
            <span class="row-label">字号</span>
            <div class="segment" data-group="fontScale" role="group" aria-label="字号">
              ${FONT_SCALE_PRESETS.map(
                (p) => `
                <button type="button" data-value="${p.value}"
                  aria-pressed="${app.fontScale === p.value}">${p.label}</button>
              `,
              ).join('')}
            </div>
          </div>
        </div>
      </section>

      <section class="group">
        <h2 class="group-title">数据</h2>
        <div class="card">
          <button class="row" type="button" data-load-demo ${this._busy ? 'disabled' : ''}>
            <span class="row-label">载入演示数据</span>
            <span class="row-value">写入 ${DEMO_COUNT} 位联系人
              <svg class="arrow" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </span>
          </button>
          <button class="row" type="button" data-clear ${this._busy ? 'disabled' : ''}>
            <span class="row-label danger-text">清空全部数据</span>
            <span class="row-value">
              <svg class="arrow" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </span>
          </button>
        </div>
        <p class="stat" data-stat>${this.esc(this._stat)}</p>
      </section>

      ${this.renderAISection()}

      ${this.renderVoiceSection()}

      ${this.renderDebugSection()}

      <p class="foot">灵讯 v0.1.0 · 用一句话操控整个应用</p>
    `;
  }

  /**
   * 开发者分组 —— 调试模式开关。
   *
   * 为什么要单独一个分组而不是塞进「外观」：
   * 它是一个**改变能力边界**的开关（开了就有 IL 直连入口），
   * 和「换个主题」不是一个量级的事，值得有自己的标题与说明。
   */
  private renderDebugSection(): string {
    const app = settingsRepo.getApp();
    const on = app.debugMode;

    return `
      <section class="group">
        <h2 class="group-title">开发者</h2>
        <div class="card">
          <div class="row">
            <span class="row-label">调试模式</span>
            <button class="switch" type="button" data-toggle-debug
              role="switch" aria-checked="${on}" aria-label="调试模式">
              <span class="knob"></span>
            </button>
          </div>
        </div>
        <p class="notice-inline">
          ${
            on
              ? '已开启。输入栏右侧出现「IL」按钮：可以绕过 AI 直接执行指令，也能随时打开调试页看能力清单与调用日志。'
              : '开启后，输入栏右侧会出现「IL」按钮 —— 绕过大模型直接执行指令，并查看完整能力清单。用于排查「AI 翻译错了」还是「宿主 API 本身有问题」。'
          }
        </p>
      </section>
    `;
  }

  /**
   * AI 服务分组 —— 真实可用。
   *
   * 三个字段对应 D1「兼容 OpenAI 协议的第三方」：
   *   服务地址 / API Key / 模型名
   * 换服务商只需改这三项，SDK 与代码不动。
   */
  private renderAISection(): string {
    const ai = settingsRepo.getAI();
    const test = this._aiTest;

    return `
      <section class="group">
        <h2 class="group-title">AI 服务</h2>
        <div class="card">
          <label class="row field">
            <span class="row-label">服务地址</span>
            <input type="url" data-ai="baseUrl" inputmode="url" spellcheck="false"
              placeholder="https://api.example.com/v1"
              value="${this.esc(ai.baseUrl)}">
          </label>
          <label class="row field">
            <span class="row-label">API Key</span>
            <input type="password" data-ai="apiKey" spellcheck="false"
              placeholder="sk-..." autocomplete="off"
              value="${this.esc(ai.apiKey)}">
          </label>
          <label class="row field">
            <span class="row-label">模型名</span>
            <input type="text" data-ai="model" spellcheck="false"
              placeholder="gpt-4o-mini / deepseek-chat …"
              value="${this.esc(ai.model)}">
          </label>
          <button class="row action" type="button" data-test-connect
            ${test.state === 'testing' ? 'disabled' : ''}>
            ${test.state === 'testing' ? '正在测试…' : '测试连接'}
          </button>
          ${
            test.state === 'ok' || test.state === 'fail'
              ? `<p class="feedback ${test.state}">${this.esc(test.text)}</p>`
              : ''
          }
        </div>
        <p class="notice-inline">
          兼容 OpenAI 协议的任意服务商均可。Key 只保存在本机浏览器里，
          但浏览器直连会把 Key 发往服务商 —— 这是 PoC 的取舍，正式产品应走服务端代理。
        </p>
      </section>
    `;
  }

  /**
   * 语音识别分组 —— 真实可用（M4）。
   *
   * 豆包流式 ASR 需要三个必填 header（D13）：
   *   · API Key     —— 这里填
   *   · Resource ID —— 这里填（默认已给对）
   *   · 转发服务地址 —— 这里填，因为浏览器不能直接带鉴权头发 WebSocket
   *
   * 转发服务是本地最小的 Node 服务（D12），它只做一件事：
   *   把 WebSocket 升级请求里的 query 参数搬进 header，再转发给火山。
   * 所以这里的地址是 `ws://localhost:8788` 这种本地地址。
   */
  private renderVoiceSection(): string {
    const asr = settingsRepo.getASR();

    return `
      <section class="group">
        <h2 class="group-title">语音识别</h2>
        <div class="card">
          <label class="row field">
            <span class="row-label">API Key</span>
            <input type="password" data-asr="apiKey" spellcheck="false"
              placeholder="豆包语音服务的 API Key" autocomplete="off"
              value="${this.esc(asr.apiKey)}">
          </label>
          <label class="row field">
            <span class="row-label">资源 ID</span>
            <input type="text" data-asr="resourceId" spellcheck="false"
              placeholder="volc.seedasr.sauc.duration"
              value="${this.esc(asr.resourceId)}">
          </label>
          <label class="row field">
            <span class="row-label">本地转发地址</span>
            <input type="url" data-asr="proxyUrl" inputmode="url" spellcheck="false"
              placeholder="ws://localhost:8788"
              value="${this.esc(asr.proxyUrl)}">
          </label>
        </div>
        <p class="notice-inline">
          浏览器无法在 WebSocket 上自定义鉴权头，所以需要先在项目里运行
          <code>npm run asr-proxy</code> 起一个本地转发服务（默认 ws://localhost:8788），
          由它把凭据搬进请求头再转发给豆包。麦克风需要 HTTPS 或 localhost 才能使用。
        </p>
      </section>
    `;
  }

  protected onMounted(): void {
    // 主题切换
    this.$$('[data-group="theme"] button').forEach((btn) => {
      btn.addEventListener('click', () => {
        const value = btn.getAttribute('data-value') as ThemeMode;
        settingsRepo.setApp({ theme: value });
        const resolved = applyTheme(value);
        this.emit('theme:changed', { mode: value, resolved });
        this.update();
      });
    });

    // 字号切换 —— 只改 --fs-base 一处，全局同步
    this.$$('[data-group="fontScale"] button').forEach((btn) => {
      btn.addEventListener('click', () => {
        const value = Number(btn.getAttribute('data-value'));
        settingsRepo.setApp({ fontScale: value });
        applyFontScale(value);
        this.emit('font-scale:changed', { scale: value });
        this.update();
      });
    });

    // 载入演示数据 —— 用户主动发起，写入的是真实数据，不是 mock
    this.$('[data-load-demo]')?.addEventListener('click', () => void this.loadDemo());

    // 清空全部数据 —— 高危，必须二次确认。
    // 确认动作随载荷一起送到模态框，避免用全局标志位猜「这次确认是为了什么」
    this.$('[data-clear]')?.addEventListener('click', () => {
      this.emit('ui:modal-open', {
        level: 'confirm',
        title: '清空全部数据',
        text: '这会删除本机所有联系人、会话、消息与文件，且无法撤销。确定继续吗？',
        onConfirm: () => void this.clearAll(),
      });
    });

    /* ---------- AI 配置 ---------- */

    // 输入即存（不设保存按钮）。每次改动都落 localStorage，
    // 这样用户填完就能直接去输入栏用，不需要记得点「保存」。
    this.$$('[data-ai]').forEach((el) => {
      const input = el as HTMLInputElement;
      const key = input.getAttribute('data-ai') as 'baseUrl' | 'apiKey' | 'model';
      input.addEventListener('input', () => {
        settingsRepo.setAI({ [key]: input.value.trim() });
        // 配置变了，之前的测试结果就过期了
        if (this._aiTest.state !== 'idle') {
          this._aiTest = { state: 'idle', text: '' };
        }
        // 广播由 settingsRepo.setAI 自己发（铁律：谁写库谁广播），
        // 这里不再重复 emit，否则同一次输入会有两个订阅回调。
      });
      // 失焦时 trim 一下再写，避免粘贴带入空格
      input.addEventListener('blur', () => {
        const v = input.value.trim();
        if (v !== input.value) {
          input.value = v;
          settingsRepo.setAI({ [key]: v });
        }
      });
    });

    this.$('[data-test-connect]')?.addEventListener('click', () => void this.testAI());

    /* ---------- 调试模式开关 ---------- */
    // 开/关都会由 settingsRepo.setApp 广播 ai:config-changed，
    // 让输入栏重渲染出/收起 IL 按钮。
    // 复用这个事件而不是新造一个：语义上都是「影响输入栏形态的设置变了」，
    // 多一个事件名只会让订阅方到处漏接。
    this.$('[data-toggle-debug]')?.addEventListener('click', () => {
      const next = !settingsRepo.getApp().debugMode;
      settingsRepo.setApp({ debugMode: next });
      this.update();

      // 控制台也留一句 —— 调试模式本身就是给开发者用的，
      // 打开/关闭这个动作本身值得在控制台有个记录，
      // 免得用户不确定「我到底开没开」。
      console.info(
        next
          ? '[灵讯] 调试模式已开启：输入栏出现「IL」按钮，解释器原始响应会打印到本控制台。'
          : '[灵讯] 调试模式已关闭。',
      );
    });

    /* ---------- 语音识别配置 ---------- */
    // 与 AI 配置一样：输入即存，不需要保存按钮
    this.$$('[data-asr]').forEach((el) => {
      const input = el as HTMLInputElement;
      const key = input.getAttribute('data-asr') as 'apiKey' | 'resourceId' | 'proxyUrl';
      input.addEventListener('input', () => {
        settingsRepo.setASR({ [key]: input.value.trim() });
      });
      input.addEventListener('blur', () => {
        const v = input.value.trim();
        if (v !== input.value) {
          input.value = v;
          settingsRepo.setASR({ [key]: v });
        }
      });
    });
  }

  /** 连通性测试 —— 发一条极短的真实请求 */
  private async testAI(): Promise<void> {
    if (this._aiTest.state === 'testing') return;
    this._aiTest = { state: 'testing', text: '' };
    this.update();

    try {
      const r = await testConnection();
      this._aiTest = {
        state: 'ok',
        text: `连接正常 · 模型 ${r.model} · 耗时 ${r.latencyMs}ms`,
      };
      // 输入栏的「未配置」提示条要实时消失。
      // 测试通过不代表配置被改写（isAIReady 只看三项是否非空），
      // 但用户可能刚填完第三项还没来得及触发 input，这里补一次广播。
      this.emit('ai:config-changed', {});
    } catch (err) {
      const reason = err instanceof AIError ? err.reason : String(err);
      const head = err instanceof AIError ? err.message : '测试失败';
      this._aiTest = { state: 'fail', text: `${head}：${reason}` };
    } finally {
      this.update();
    }
  }

  private async loadDemo(): Promise<void> {
    if (this._busy) return;
    this._busy = true;
    this.update();

    try {
      const r = await loadDemoData();
      await this.refreshStat();
      this.emit('ui:modal-open', {
        level: 'info',
        title: '演示数据已载入',
        text: `写入了 ${r.contacts} 位联系人与 ${r.sessions} 个会话。这些是真实数据，可以正常发消息、被 AI 读写。`,
      });
    } catch (err) {
      this.emit('ui:modal-open', {
        level: 'error',
        title: '载入失败',
        text: err instanceof Error ? err.message : '未知错误',
      });
    } finally {
      this._busy = false;
      this.update();
    }
  }

  private async clearAll(): Promise<void> {
    this._busy = true;
    this.update();

    try {
      await dbClearAll();
      await this.refreshStat();
      eventBus.emit('contact:list-changed', {});
      eventBus.emit('session:list-changed', {});
      eventBus.emit('data:cleared', {});
      this.emit('ui:modal-open', {
        level: 'info',
        title: '已清空',
        text: '本机数据已全部删除。',
      });
    } catch (err) {
      this.emit('ui:modal-open', {
        level: 'error',
        title: '清空失败',
        text: err instanceof Error ? err.message : '未知错误',
      });
    } finally {
      this._busy = false;
      this.update();
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
    void this.refreshStat().then(() => this.update());
  }
}

define('lx-page-settings', LxPageSettings);
